import {
  useDocument,
  useGetDevice,
  useGetUsersByOrg,
  useTiptapCollaborationDocument,
} from "@hooks";
import type { BaseUser, Document, DocumentVersion } from "@models";
import { useEditorState } from "@tiptap/react";
import { DocumentStatus } from "@types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Panel, PanelGroup } from "react-resizable-panels";
import { useParams } from "react-router-dom";
import { useGetUser } from "src/hooks/queries";
import {
  getDynamicContent,
  notifyTiptapCommentMentions,
} from "src/services/backend";
import { useStore } from "src/stores";
import { DynamicNodeEditorProvider } from "src/tiptap/components/tiptap-node/dynamic-nodes";
import type { DynamicContentResolution } from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-context";
import { getReadyEditor } from "src/tiptap/components/tiptap-templates/simple/editor-readiness";
import {
  SimpleEditor,
  type SimpleEditorVersionsContext,
} from "src/tiptap/components/tiptap-templates/simple/simple-editor";
import { useSimpleEditorVersions } from "src/tiptap/components/tiptap-templates/simple/simple-editor-versions";
import type { VersionComparisonDynamicContentResolver } from "src/tiptap/components/tiptap-templates/simple/version-comparison/dynamic-version-comparison";
import { TiptapApprovalDrawer } from "./TiptapApprovalDrawer";
import { TiptapCollaborationPanel } from "./TiptapCollaborationPanel";
import { TiptapDocViewerTopBar } from "./TiptapDocViewerTopBar";
import { TiptapEditorLoadingSkeleton } from "./TiptapEditorLoadingSkeleton";
import { TiptapVersionsPanel } from "./TiptapVersionsPanel";
import type {
  TiptapCommentsController,
  TiptapSuggestionsController,
} from "./collaboration";
import { getTiptapCollaborationActivityCount } from "./collaboration/tiptapCommentUtils";
import { shouldShowTiptapApprovalPing } from "./tiptapApprovalPing";
import { getTiptapLifecycleActionState } from "./tiptapLifecycleActions";
import {
  getBackendDocumentVersionForTiptapVersion,
  getTiptapVersionCardAuthorByVersion,
  getTiptapVersionCardRestorableByVersion,
  getTiptapVersionCardStatusByVersion,
  getTiptapVersionCardTitleByVersion,
  shouldLockTiptapVersionPreview,
} from "./tiptapVersionCardMappings";
import {
  useSyncTiptapEditorRoute,
  useTiptapEditorRouteParams,
  useTiptapEditorRouteSelection,
} from "./useTiptapEditorRoute";
import { useTiptapRecordOptions } from "./useTiptapRecordOptions";
import { useTiptapUpdateAnswers } from "./useTiptapUpdateAnswers";

import "./TiptapEditor.css";

type TiptapEditorProps = {
  agentThreadId?: string;
  document?: Document | null;
  documentId?: string;
  isLifecycleDeleted?: boolean;
  selectedDocVersion?: DocumentVersion;
};

type TiptapSidePanel = "collaboration" | "versions";
type ApprovalVersionTransitionState = {
  isPending: boolean;
  baselineVersion: number | null;
};
type NewRevisionTransitionState = {
  isPending: boolean;
  baselineVersion: number | null;
};

// Safety net: if the backend-created new-revision head snapshot never shows up
// in the Tiptap version list (e.g. a missed broadcast), stop blocking the editor
// after this long and fall back to the normal derivation.
const NEW_REVISION_TRANSITION_TIMEOUT_MS = 10_000;

const EMPTY_TIPTAP_ORG_USERS: BaseUser[] = [];
const EMPTY_APPROVAL_VERSION_TRANSITION: ApprovalVersionTransitionState = {
  isPending: false,
  baselineVersion: null,
};
const EMPTY_NEW_REVISION_TRANSITION: NewRevisionTransitionState = {
  isPending: false,
  baselineVersion: null,
};
const EMPTY_SIMPLE_EDITOR_VERSIONS_CONTEXT: SimpleEditorVersionsContext = {
  collaborationProvider: null,
  isEditable: false,
  readyEditor: null,
  trackedChangesUserId: "current-user",
  trackedChangesUserName: "Current user",
};
const LINKED_SUGGESTION_CLASS = "tiptap-editor-linked-suggestion";

function escapeCssAttributeValue(value: string) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }

  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function isLinkedSuggestionElementVisible(element: HTMLElement) {
  return !element.closest(
    '[aria-hidden="true"], .simple-editor-content--hidden'
  );
}

function findFirstVisibleElement(selector: string) {
  const elements = window.document.querySelectorAll<HTMLElement>(selector);

  return Array.from(elements).find(isLinkedSuggestionElementVisible) ?? null;
}

function findLinkedSuggestionElement(suggestionId: string) {
  const escapedSuggestionId = escapeCssAttributeValue(suggestionId);

  return findFirstVisibleElement(
    [
      `.simple-editor-wrapper [data-suggestion-id="${escapedSuggestionId}"]`,
      `.simple-editor-wrapper [data-suggestion="${escapedSuggestionId}"]`,
    ].join(", ")
  );
}

export const TiptapEditor = ({
  agentThreadId: agentThreadIdProp,
  document,
  documentId: documentIdProp,
  isLifecycleDeleted = false,
  selectedDocVersion,
}: TiptapEditorProps = {}) => {
  const {
    deviceId: routeDeviceId = "",
    docId = "",
    orgId,
  } = useParams<{
    deviceId?: string;
    docId?: string;
    orgId: string;
  }>();
  const isDeleted = isLifecycleDeleted || Boolean(document?.softDeletionEvent);
  const deviceId = document?.deviceId ?? routeDeviceId;
  const editorRoute = useTiptapEditorRouteParams();
  const { agentStore } = useStore();
  const { data: user } = useGetUser();
  const { data: orgUsers = EMPTY_TIPTAP_ORG_USERS } = useGetUsersByOrg({
    orgId: orgId ?? "",
    enabled: Boolean(orgId),
  });
  const { data: device } = useGetDevice({
    orgId: orgId ?? "",
    deviceId,
  });
  const [openSidePanel, setOpenSidePanel] = useState<TiptapSidePanel | null>(
    () =>
      !isDeleted && editorRoute.initialComparisonVersion !== null
        ? "versions"
        : null
  );
  const [isApprovalPanelOpen, setIsApprovalPanelOpen] = useState(false);
  const [showAllConditionals, setShowAllConditionals] = useState(false);
  const [commentsController, setCommentsController] =
    useState<TiptapCommentsController | null>(null);
  const [suggestionsController, setSuggestionsController] =
    useState<TiptapSuggestionsController | null>(null);
  const [approvalVersionTransition, setApprovalVersionTransition] =
    useState<ApprovalVersionTransitionState>(EMPTY_APPROVAL_VERSION_TRANSITION);
  const [newRevisionTransition, setNewRevisionTransition] =
    useState<NewRevisionTransitionState>(EMPTY_NEW_REVISION_TRANSITION);
  const newRevisionTransitionTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [simpleEditorVersionsContext, setSimpleEditorVersionsContext] =
    useState<SimpleEditorVersionsContext>(EMPTY_SIMPLE_EDITOR_VERSIONS_CONTEXT);
  const currentEditor = simpleEditorVersionsContext.readyEditor;
  const currentEditorContentState = useEditorState({
    editor: currentEditor,
    selector: ({ editor }) => ({
      editor,
      isEmpty: editor?.isEmpty ?? true,
    }),
    equalityFn: (current, previous) =>
      current.editor === previous?.editor &&
      current.isEmpty === previous?.isEmpty,
  });
  // A newly supplied editor can render once before useEditorState switches its
  // transaction subscription. Read that editor directly during this handoff so
  // a loaded non-empty document is not stuck behind the previous null snapshot.
  const isCurrentEditorEmpty =
    currentEditorContentState?.editor === currentEditor
      ? currentEditorContentState.isEmpty
      : (currentEditor?.isEmpty ?? true);
  const appliedCommentThreadIdRef = useRef<string | null>(null);
  const appliedTiptapSuggestionIdRef = useRef<string | null>(null);
  const approvalTransitionPreviousSelectedVersionRef = useRef<number | null>(
    null
  );
  const hasCompletedApprovalVersionTransitionRef = useRef(false);
  const restoreVersionLifecycleRef = useRef<() => Promise<void>>(
    async () => {}
  );
  const tiptapDocumentId = document?.tiptapPath ?? documentIdProp ?? "";
  const agentThreadId = agentThreadIdProp ?? editorRoute.agentThreadId ?? "";
  const commentThreadIdFromUrl = editorRoute.commentThreadId;
  const tiptapSuggestionIdFromUrl = editorRoute.tiptapSuggestionId;
  const tiptapSuggestionScrollKey = tiptapSuggestionIdFromUrl
    ? [
        tiptapSuggestionIdFromUrl,
        editorRoute.navigationKey,
        selectedDocVersion?.id ?? "",
      ].join(":")
    : "";
  const isApprovalVersionTransitionPending =
    approvalVersionTransition.isPending;
  const pendingApprovalSnapshotBaselineVersion =
    // react-doctor-disable-next-line react-doctor/no-event-handler -- This baseline changes rendered version-card metadata during an approval transition; it is not used to trigger a deferred effect.
    approvalVersionTransition.baselineVersion;
  const handleTiptapCommentCreated = useCallback(
    (
      orgId: string,
      backendDocumentId: string,
      threadId: string,
      commentContent: string,
      previousCommentContent?: string
    ) => {
      void notifyTiptapCommentMentions({
        orgId,
        documentId: backendDocumentId,
        threadId,
        commentContent,
        previousCommentContent,
      }).catch((error) => {
        console.warn("Failed to notify backend about TipTap mentions", error);
      });
    },
    []
  );
  const {
    collaborationConfig,
    documentContent,
    isLoading: isDocumentLoading,
    loadError,
    orgScopedDocumentName,
  } = useTiptapCollaborationDocument({
    collaborationEnabled: !isDeleted,
    documentName: tiptapDocumentId,
    orgId,
  });
  const importedDynamicNodeAttributionUserId = useMemo(() => {
    const documentCreatorId = document?.createdBy;

    if (
      !documentCreatorId ||
      !orgUsers.some((orgUser) => orgUser.id === documentCreatorId)
    ) {
      return null;
    }

    return documentCreatorId;
  }, [document?.createdBy, orgUsers]);

  const getVersionCardStatusByVersion = useCallback(
    (latestTiptapVersion: number | null) =>
      getTiptapVersionCardStatusByVersion({
        document,
        latestTiptapVersion,
        pendingApprovalSnapshotBaselineVersion,
      }),
    [document, pendingApprovalSnapshotBaselineVersion]
  );
  const getVersionCardRestorableByVersion = useCallback(
    (latestTiptapVersion: number | null) =>
      getTiptapVersionCardRestorableByVersion({
        document,
        latestTiptapVersion,
        pendingApprovalSnapshotBaselineVersion,
      }),
    [document, pendingApprovalSnapshotBaselineVersion]
  );
  const getVersionCardAuthorByVersion = useCallback(
    (latestTiptapVersion: number | null) =>
      getTiptapVersionCardAuthorByVersion({
        document,
        latestTiptapVersion,
        pendingApprovalSnapshotBaselineVersion,
      }),
    [document, pendingApprovalSnapshotBaselineVersion]
  );
  const getVersionCardTitleByVersion = useCallback(
    (latestTiptapVersion: number | null) =>
      getTiptapVersionCardTitleByVersion({
        document,
        latestTiptapVersion,
        pendingApprovalSnapshotBaselineVersion,
      }),
    [document, pendingApprovalSnapshotBaselineVersion]
  );
  const handleRestoreVersionLifecycle = useCallback(
    async () => restoreVersionLifecycleRef.current(),
    []
  );
  // Version comparison resolves dynamic chips against each compared snapshot.
  // Map the Tiptap snapshot back to the matching backend document version first.
  const resolveDynamicContentForVersion =
    useCallback<VersionComparisonDynamicContentResolver>(
      async ({ dataKeys, latestTiptapVersion, version }) => {
        if (!orgId || !document?.id) {
          return null;
        }
        const backendVersion = getBackendDocumentVersionForTiptapVersion({
          document,
          latestTiptapVersion,
          selectedTiptapVersion: version,
        });

        const { data } = await getDynamicContent(orgId, document.id, {
          versionId: backendVersion?.id,
          dataKeys,
        });
        return data && typeof data === "object"
          ? (data as DynamicContentResolution)
          : null;
      },
      [document, orgId]
    );
  const editorVersions = useSimpleEditorVersions({
    collaborationProvider: simpleEditorVersionsContext.collaborationProvider,
    getVersionCardAuthorByVersion,
    getVersionCardRestorableByVersion,
    getVersionCardStatusByVersion,
    getVersionCardTitleByVersion,
    isEnabled: Boolean(collaborationConfig),
    isEditable: simpleEditorVersionsContext.isEditable,
    onRestoreVersion: handleRestoreVersionLifecycle,
    orgUsers,
    readyEditor: simpleEditorVersionsContext.readyEditor,
    resolveDynamicContentForVersion,
    trackedChangesUserId: simpleEditorVersionsContext.trackedChangesUserId,
    trackedChangesUserName: simpleEditorVersionsContext.trackedChangesUserName,
    versionsRefreshKey: tiptapDocumentId,
  });
  const routeSelection = useTiptapEditorRouteSelection({
    compareVersion: editorVersions.compareVersion,
    initialComparisonVersion: editorRoute.initialComparisonVersion,
    initialSelectedVersion: editorRoute.initialSelectedVersion,
    isVersionHistorySynced: editorVersions.isVersionHistorySynced,
    selectVersion: editorVersions.selectVersion,
    versions: editorVersions.versions,
    versionsRefreshKey: tiptapDocumentId,
  });
  const selectedTiptapVersion = isApprovalVersionTransitionPending
    ? editorVersions.selectedVersion
    : (editorVersions.selectedVersion ?? editorVersions.currentVersion);
  const selectedVersionCardStatusByVersion = useMemo(
    () => getVersionCardStatusByVersion(editorVersions.currentVersion),
    [editorVersions.currentVersion, getVersionCardStatusByVersion]
  );
  const selectedVersionCardStatus =
    selectedTiptapVersion !== null
      ? (selectedVersionCardStatusByVersion.get(selectedTiptapVersion) ?? null)
      : null;
  const latestDocumentVersion = document?.versions?.[0] ?? null;
  const isLockedLatestVersionSelected = shouldLockTiptapVersionPreview({
    latestTiptapVersion: editorVersions.currentVersion,
    status: selectedVersionCardStatus,
    version: selectedTiptapVersion,
  });
  const canShowUpdateAnswersAction =
    selectedVersionCardStatus === "Routing" ||
    selectedVersionCardStatus === "Editable";
  const selectedSnapshotDocVersion = useMemo(
    () =>
      getBackendDocumentVersionForTiptapVersion({
        document,
        latestTiptapVersion: editorVersions.currentVersion,
        selectedTiptapVersion: editorVersions.selectedVersion,
      }),
    [document, editorVersions.currentVersion, editorVersions.selectedVersion]
  );
  const approvalDocVersion =
    editorVersions.selectedVersion === null
      ? latestDocumentVersion
      : selectedSnapshotDocVersion;
  const hasOutstandingApproval = shouldShowTiptapApprovalPing({
    isRoutingVersion: selectedVersionCardStatus === "Routing",
    selectedDocVersion: approvalDocVersion,
    userId: user?.id,
  });
  const dynamicContentDocVersion =
    editorVersions.selectedVersion === null
      ? (selectedDocVersion ?? document?.versions?.[0] ?? null)
      : (selectedSnapshotDocVersion ??
        document?.versions?.[0] ??
        selectedDocVersion ??
        null);
  const recordOptions = useTiptapRecordOptions({
    deviceId,
    document,
    enabled: !isDeleted,
    orgId,
  });
  const hasApprovalPanelData = Boolean(document && approvalDocVersion);
  // After "New revision" the backend creates the draft row AND a fresh Tiptap
  // head above the live revision's pinned version. The draft appears in
  // `document.versions[0]` immediately (optimistic cache write), but the new
  // head only reaches `currentVersion` once Tiptap Cloud broadcasts it. Until it
  // does, the draft head still sits on the live revision's pinned version, so the
  // version rail would briefly collapse the two (the live "Live" card flashing as
  // "Editable"). Hold the loading/locked surface until the head advances past the
  // baseline captured at click time.
  const hasNewRevisionHeadArrived =
    editorVersions.currentVersion !== null &&
    (newRevisionTransition.baselineVersion === null ||
      editorVersions.currentVersion > newRevisionTransition.baselineVersion);
  const isAwaitingNewRevisionHead =
    newRevisionTransition.isPending && !hasNewRevisionHeadArrived;
  const isRouteSelectionLoading =
    routeSelection.isRouteSelectionPending &&
    (editorRoute.initialComparisonVersion !== null
      ? editorVersions.comparison?.sourceVersion !==
        editorRoute.initialComparisonVersion
      : editorRoute.initialSelectedVersion !== null &&
        editorVersions.selectedVersion !== editorRoute.initialSelectedVersion);
  const isTiptapEditorLoading =
    isDocumentLoading || editorVersions.isLoading || isRouteSelectionLoading;
  const isApprovalActionDisabled =
    isTiptapEditorLoading ||
    isCurrentEditorEmpty !== false ||
    !hasApprovalPanelData;
  const {
    canCreateNewRevision,
    createNewRevision,
    documentStatus,
    isCreatingNewRevision,
    isLocked,
    mostRecentVersion,
    isResumingEditing,
    resumeEditing,
    saveOfficialVersion,
  } = useDocument({
    document,
    selectedDocVersion: latestDocumentVersion ?? undefined,
    orgId,
    deviceId,
  });
  const updateAnswersDocVersion =
    approvalDocVersion ?? selectedDocVersion ?? mostRecentVersion;
  const shouldDeriveUpdateAnswersStatus =
    updateAnswersDocVersion &&
    updateAnswersDocVersion.id !== approvalDocVersion?.id;
  const updateAnswersDocVersionStatus =
    updateAnswersDocVersion && shouldDeriveUpdateAnswersStatus
      ? typeof updateAnswersDocVersion.revision === "number"
        ? DocumentStatus.LIVE
        : updateAnswersDocVersion.readyForApproval
          ? DocumentStatus.UNAPPROVED
          : DocumentStatus.DRAFT
      : documentStatus;
  const isCollaborationPanelOpen = openSidePanel === "collaboration";
  const isVersionsPanelOpen = openSidePanel === "versions";
  const lifecycleActions = getTiptapLifecycleActionState({
    canCreateBackendRevision: canCreateNewRevision,
    hasReadyEditor: Boolean(simpleEditorVersionsContext.readyEditor),
    isBackendVersionLocked: isLocked,
    isVersionHistorySynced: editorVersions.isVersionHistorySynced,
    latestTiptapVersion: editorVersions.currentVersion,
    selectedTiptapVersion: editorVersions.selectedVersion,
  });
  const canShowResumeEditingAction =
    isLockedLatestVersionSelected && lifecycleActions.canResumeEditing;
  const shouldResumeEditingAfterRestore = isLocked && !canCreateNewRevision;
  restoreVersionLifecycleRef.current = async () => {
    if (!shouldResumeEditingAfterRestore) {
      return;
    }

    setIsApprovalPanelOpen(false);
    await resumeEditing();
  };
  const openCollaborationPanel = useCallback(() => {
    if (isDeleted) {
      return;
    }

    setIsApprovalPanelOpen(false);
    setOpenSidePanel("collaboration");
  }, [isDeleted]);
  const handleTiptapSuggestionClick = useCallback(
    (suggestionId: string) => {
      setIsApprovalPanelOpen(false);
      setOpenSidePanel("collaboration");
      commentsController?.selectThread(suggestionId);

      const nextSearchParams = new URLSearchParams(editorRoute.searchParams);
      nextSearchParams.set("tiptapSuggestionId", suggestionId);
      nextSearchParams.delete("commentThreadId");
      editorRoute.setSearchParams(nextSearchParams);
    },
    [commentsController, editorRoute.searchParams, editorRoute.setSearchParams]
  );
  const toggleCollaborationPanel = useCallback(() => {
    if (isDeleted) {
      return;
    }

    setIsApprovalPanelOpen(false);
    setOpenSidePanel((currentPanel) =>
      currentPanel === "collaboration" ? null : "collaboration"
    );
  }, [isDeleted]);
  const toggleVersionsPanel = useCallback(() => {
    if (isDeleted) {
      return;
    }

    setIsApprovalPanelOpen(false);
    setOpenSidePanel((currentPanel) =>
      currentPanel === "versions" ? null : "versions"
    );
  }, [isDeleted]);
  const openApprovalPanel = useCallback(() => {
    if (isApprovalActionDisabled) {
      return;
    }

    setIsApprovalPanelOpen(true);
  }, [isApprovalActionDisabled]);
  const restoreApprovalVersionTransitionSelection = useCallback(() => {
    const previousSelectedVersion =
      approvalTransitionPreviousSelectedVersionRef.current;

    if (previousSelectedVersion !== null) {
      editorVersions.selectVersion(previousSelectedVersion);
      return;
    }

    editorVersions.clearSelectedVersion();
  }, [editorVersions.clearSelectedVersion, editorVersions.selectVersion]);
  const beginApprovalVersionTransition = useCallback(() => {
    approvalTransitionPreviousSelectedVersionRef.current =
      editorVersions.selectedVersion;
    hasCompletedApprovalVersionTransitionRef.current = false;
    setApprovalVersionTransition({
      isPending: true,
      baselineVersion: editorVersions.currentVersion,
    });
    editorVersions.clearSelectedVersion();
  }, [
    editorVersions.clearSelectedVersion,
    editorVersions.currentVersion,
    editorVersions.selectedVersion,
  ]);
  const settleApprovalVersionTransition = useCallback(() => {
    if (!hasCompletedApprovalVersionTransitionRef.current) {
      restoreApprovalVersionTransitionSelection();
    }

    hasCompletedApprovalVersionTransitionRef.current = false;
    approvalTransitionPreviousSelectedVersionRef.current = null;
    setApprovalVersionTransition(EMPTY_APPROVAL_VERSION_TRANSITION);
  }, [restoreApprovalVersionTransitionSelection]);
  const handleBeforeSubmitApproval = useCallback(async () => {
    const didStartTransition = isApprovalVersionTransitionPending;

    if (!didStartTransition) {
      beginApprovalVersionTransition();
    }

    try {
      const savedTiptapVersion = await saveOfficialVersion();

      if (savedTiptapVersion !== null) {
        hasCompletedApprovalVersionTransitionRef.current = true;
        editorVersions.selectVersion(savedTiptapVersion);
        return;
      }

      restoreApprovalVersionTransitionSelection();
    } catch (error) {
      restoreApprovalVersionTransitionSelection();
      throw error;
    } finally {
      if (!didStartTransition) {
        settleApprovalVersionTransition();
      }
    }
  }, [
    beginApprovalVersionTransition,
    editorVersions.selectVersion,
    isApprovalVersionTransitionPending,
    restoreApprovalVersionTransitionSelection,
    saveOfficialVersion,
    settleApprovalVersionTransition,
  ]);
  const handleResumeEditing = useCallback(async () => {
    setIsApprovalPanelOpen(false);
    editorVersions.clearSelectedVersion();
    await resumeEditing();
  }, [editorVersions.clearSelectedVersion, resumeEditing]);
  const {
    canUpdateAnswers,
    confirmationModal: updateAnswersConfirmationModal,
    isPending: isUpdateAnswersPending,
    onUpdateAnswersClick,
  } = useTiptapUpdateAnswers({
    document,
    selectedVersion: updateAnswersDocVersion ?? undefined,
    orgId: orgId ?? "",
    deviceId,
    docVersionStatus: updateAnswersDocVersionStatus,
    enabled: !isDeleted,
  });
  const settleNewRevisionTransition = useCallback(() => {
    if (newRevisionTransitionTimeoutRef.current !== null) {
      clearTimeout(newRevisionTransitionTimeoutRef.current);
      newRevisionTransitionTimeoutRef.current = null;
    }
    setNewRevisionTransition(EMPTY_NEW_REVISION_TRANSITION);
  }, []);
  const beginNewRevisionTransition = useCallback(() => {
    if (newRevisionTransitionTimeoutRef.current !== null) {
      clearTimeout(newRevisionTransitionTimeoutRef.current);
    }
    newRevisionTransitionTimeoutRef.current = setTimeout(() => {
      newRevisionTransitionTimeoutRef.current = null;
      setNewRevisionTransition(EMPTY_NEW_REVISION_TRANSITION);
    }, NEW_REVISION_TRANSITION_TIMEOUT_MS);
    setNewRevisionTransition({
      isPending: true,
      baselineVersion: editorVersions.currentVersion,
    });
  }, [editorVersions.currentVersion]);
  // Settle the transition once the backend-created head snapshot reaches the
  // version list, at which point the rail resolves the live revision as "Live"
  // and the new head as the editable draft.
  useEffect(() => {
    if (newRevisionTransition.isPending && hasNewRevisionHeadArrived) {
      settleNewRevisionTransition();
    }
  }, [
    hasNewRevisionHeadArrived,
    newRevisionTransition.isPending,
    settleNewRevisionTransition,
  ]);
  useEffect(
    () => () => {
      if (newRevisionTransitionTimeoutRef.current !== null) {
        clearTimeout(newRevisionTransitionTimeoutRef.current);
      }
    },
    []
  );
  const handleCreateNewRevision = useCallback(async () => {
    // New revision is a backend-driven lifecycle transition: the backend creates
    // the draft DocumentVersion row (left unpinned, `tiptapVersion = null`) AND a
    // fresh Tiptap Cloud head above the live revision's pinned version, so the
    // draft gets its own version number and the live revision keeps its "Live"
    // card. Doing the version creation on the backend also lets non-browser
    // callers (e.g. the MCP service) start a revision without a running editor.
    // We only coordinate the loading state here so the rail doesn't flash the old
    // collision while the new head propagates.
    if (!lifecycleActions.canCreateNewRevision) {
      return;
    }

    setIsApprovalPanelOpen(false);
    editorVersions.clearSelectedVersion();
    beginNewRevisionTransition();
    try {
      await createNewRevision();
    } catch (error) {
      settleNewRevisionTransition();
      throw error;
    }
  }, [
    beginNewRevisionTransition,
    createNewRevision,
    editorVersions.clearSelectedVersion,
    lifecycleActions.canCreateNewRevision,
    settleNewRevisionTransition,
  ]);
  const sidePanelDefaultSize = 25;
  const hasSidePanelOpen = openSidePanel !== null;
  const collaborationActivityCount = getTiptapCollaborationActivityCount({
    includeResolvedComments: false,
    suggestions: suggestionsController?.suggestions ?? [],
    threads: commentsController?.threads ?? [],
  });
  const visibleTiptapEditor = editorVersions.isVersionPreviewActive
    ? getReadyEditor(editorVersions.previewEditor)
    : simpleEditorVersionsContext.readyEditor;
  const areTiptapEditorActionsDisabled =
    Boolean(loadError) || isTiptapEditorLoading;

  useEffect(() => {
    if (orgScopedDocumentName) {
      agentStore.setActiveTiptapDocumentContext({
        documentId: orgScopedDocumentName,
      });

      return () => {
        agentStore.clearActiveTiptapDocumentContext(orgScopedDocumentName);
      };
    }

    return () => {
      agentStore.clearActiveTiptapDocumentContext();
    };
  }, [agentStore, orgScopedDocumentName]);

  useEffect(() => {
    setSimpleEditorVersionsContext(EMPTY_SIMPLE_EDITOR_VERSIONS_CONTEXT);
    setIsApprovalPanelOpen(false);
    setOpenSidePanel(null);
    setShowAllConditionals(false);
    appliedTiptapSuggestionIdRef.current = null;
  }, [tiptapDocumentId]);

  useEffect(() => {
    if (isDeleted) {
      setIsApprovalPanelOpen(false);
      setOpenSidePanel(null);
      return;
    }

    if (editorRoute.initialComparisonVersion !== null) {
      setOpenSidePanel("versions");
    }
  }, [editorRoute.initialComparisonVersion, isDeleted, tiptapDocumentId]);

  useEffect(() => {
    if (
      isApprovalPanelOpen &&
      (!hasApprovalPanelData || isApprovalActionDisabled)
    ) {
      setIsApprovalPanelOpen(false);
    }
  }, [hasApprovalPanelData, isApprovalActionDisabled, isApprovalPanelOpen]);

  useSyncTiptapEditorRoute({
    activeComparisonVersion: editorVersions.comparison?.sourceVersion ?? null,
    document,
    isRouteSelectionPending: routeSelection.isRouteSelectionPending,
    latestTiptapVersion: editorVersions.currentVersion,
    searchParams: editorRoute.searchParams,
    selectedTiptapVersion: editorVersions.selectedVersion,
    setSearchParams: editorRoute.setSearchParams,
  });

  useEffect(() => {
    if (!commentThreadIdFromUrl) {
      appliedCommentThreadIdRef.current = null;
      return;
    }

    if (
      !commentsController ||
      appliedCommentThreadIdRef.current === commentThreadIdFromUrl
    ) {
      return;
    }

    const linkedThreadExists = [
      ...commentsController.threads,
      ...commentsController.suggestionThreads,
    ].some((thread) => thread.id === commentThreadIdFromUrl);

    if (!linkedThreadExists) {
      return;
    }

    appliedCommentThreadIdRef.current = commentThreadIdFromUrl;
    // react-doctor-disable-next-line react-doctor/no-chain-state-updates -- Deep links can open the panel only after the editor controller publishes matching thread metadata.
    setIsApprovalPanelOpen(false);
    setOpenSidePanel("collaboration");

    // react-doctor-disable-next-line react-doctor/no-event-handler -- This bridges router state to the TipTap controller once the linked thread exists.
    if (commentsController.selectedThreadId !== commentThreadIdFromUrl) {
      commentsController.selectThread(commentThreadIdFromUrl);
    }
  }, [commentThreadIdFromUrl, commentsController]);

  useEffect(() => {
    if (!tiptapSuggestionIdFromUrl) {
      appliedTiptapSuggestionIdRef.current = null;
      return;
    }

    if (
      isTiptapEditorLoading ||
      !visibleTiptapEditor ||
      appliedTiptapSuggestionIdRef.current === tiptapSuggestionScrollKey
    ) {
      return;
    }

    let retryTimeoutId: number | null = null;
    let pulseTimeoutId: number | null = null;
    let highlightedElement: HTMLElement | null = null;
    let attempts = 0;
    let isCancelled = false;

    const scrollToSuggestion = () => {
      if (isCancelled) {
        return;
      }

      const targetElement = findLinkedSuggestionElement(
        tiptapSuggestionIdFromUrl
      );

      if (targetElement) {
        appliedTiptapSuggestionIdRef.current = tiptapSuggestionScrollKey;
        highlightedElement = targetElement;
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        targetElement.classList.add(LINKED_SUGGESTION_CLASS);
        pulseTimeoutId = window.setTimeout(() => {
          targetElement.classList.remove(LINKED_SUGGESTION_CLASS);
        }, 2000);
        return;
      }

      attempts += 1;
      if (attempts < 10) {
        retryTimeoutId = window.setTimeout(scrollToSuggestion, 100);
      }
    };

    scrollToSuggestion();

    return () => {
      isCancelled = true;

      if (retryTimeoutId !== null) {
        window.clearTimeout(retryTimeoutId);
      }

      if (pulseTimeoutId !== null) {
        window.clearTimeout(pulseTimeoutId);
      }

      highlightedElement?.classList.remove(LINKED_SUGGESTION_CLASS);
    };
  }, [
    isTiptapEditorLoading,
    tiptapSuggestionScrollKey,
    tiptapSuggestionIdFromUrl,
    visibleTiptapEditor,
  ]);

  if (!orgId || !docId) {
    return <TiptapEditorLoadingSkeleton documentId={tiptapDocumentId} />;
  }

  const shouldUseDeviceScopeLabel = Boolean(
    document?.deviceId || recordOptions
  );
  const documentScopeLabel = document
    ? shouldUseDeviceScopeLabel
      ? device?.name?.trim() || "Device"
      : "Global"
    : null;
  return (
    <PanelGroup direction="horizontal" className="flex flex-1 min-h-0 w-full">
      <Panel
        id="tiptap-doc-viewer-main"
        order={0}
        defaultSize={hasSidePanelOpen ? 100 - sidePanelDefaultSize : 100}
        minSize={50}
        className="min-w-0"
      >
        <div
          className="tiptap-editor-page relative flex h-full min-h-0 flex-1 flex-col overflow-hidden"
          data-show-all-conditionals={showAllConditionals}
        >
          <DynamicNodeEditorProvider
            orgId={orgId}
            deviceId={deviceId}
            currentDocument={document}
            viewedVersion={dynamicContentDocVersion}
            frozen={Boolean(dynamicContentDocVersion?.readyForApproval)}
            showAllConditionals={showAllConditionals}
            onShowAllConditionalsChange={setShowAllConditionals}
          >
            <TiptapDocViewerTopBar
              canCreateNewRevision={lifecycleActions.canCreateNewRevision}
              canResumeEditing={canShowResumeEditingAction}
              collaborationActivityCount={collaborationActivityCount}
              comparison={editorVersions.comparison}
              copyEditor={visibleTiptapEditor}
              document={document}
              documentScopeLabel={documentScopeLabel}
              hasOutstandingApproval={hasOutstandingApproval}
              hideUnavailableEditButton={
                simpleEditorVersionsContext.isEditable &&
                editorVersions.selectedVersion === null
              }
              isActionsLoading={isTiptapEditorLoading}
              isApprovalDisabled={isApprovalActionDisabled}
              isCollaborationPanelOpen={isCollaborationPanelOpen}
              isCopyDisabled={areTiptapEditorActionsDisabled}
              isCreatingNewRevision={isCreatingNewRevision}
              isExportDisabled={areTiptapEditorActionsDisabled}
              isDeleted={isDeleted}
              isResumingEditing={isResumingEditing}
              isVersionsPanelOpen={isVersionsPanelOpen}
              onApprovalClick={openApprovalPanel}
              onCreateNewRevision={handleCreateNewRevision}
              onResumeEditing={handleResumeEditing}
              onToggleCollaborationPanel={toggleCollaborationPanel}
              onToggleVersionsPanel={toggleVersionsPanel}
              previewAction={editorVersions.previewAction}
              recordOptions={recordOptions}
              selectedDocVersion={dynamicContentDocVersion}
              updateAnswersAction={
                canShowUpdateAnswersAction && canUpdateAnswers
                  ? {
                      isLoading: isUpdateAnswersPending,
                      onClick: onUpdateAnswersClick,
                    }
                  : null
              }
            />
            {loadError && (
              <div className="tiptap-editor-status tiptap-editor-status--error">
                {loadError}
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-hidden">
              <SimpleEditor
                agentThreadId={agentThreadId || undefined}
                areCommentMutationsDisabled={isDeleted}
                collaborationConfig={collaborationConfig ?? undefined}
                documentContent={documentContent}
                documentId={tiptapDocumentId || undefined}
                importedDynamicNodeAttributionUserId={
                  importedDynamicNodeAttributionUserId
                }
                isLoading={isTiptapEditorLoading || isAwaitingNewRevisionHead}
                isReadOnly={
                  isDeleted ||
                  !(latestDocumentVersion?.revision === null) ||
                  isLockedLatestVersionSelected ||
                  isAwaitingNewRevisionHead
                }
                loadingLabel={
                  isAwaitingNewRevisionHead
                    ? "Starting new revision"
                    : isTiptapEditorLoading && !isDocumentLoading
                      ? "Syncing Tiptap versions"
                      : undefined
                }
                onCommentCreated={(...args) =>
                  handleTiptapCommentCreated(orgId, docId, ...args)
                }
                onCommentsControllerChange={setCommentsController}
                onOpenCommentsPanel={openCollaborationPanel}
                onSuggestionClick={handleTiptapSuggestionClick}
                onSuggestionsControllerChange={setSuggestionsController}
                onVersionsContextChange={setSimpleEditorVersionsContext}
                orgId={orgId ?? ""}
                orgUsers={orgUsers}
                trackedChangesInitiallyEnabled={false}
                versionController={editorVersions}
              />
            </div>
          </DynamicNodeEditorProvider>
          {document && approvalDocVersion ? (
            <TiptapApprovalDrawer
              deviceId={deviceId}
              document={document}
              open={isApprovalPanelOpen}
              onApprovalSubmitSettled={settleApprovalVersionTransition}
              onApprovalSubmitStart={beginApprovalVersionTransition}
              onBeforeSubmitApproval={handleBeforeSubmitApproval}
              onClose={() => setIsApprovalPanelOpen(false)}
              selectedDocVersion={approvalDocVersion}
            />
          ) : null}
          {updateAnswersConfirmationModal}
        </div>
      </Panel>

      <TiptapCollaborationPanel
        commentsController={commentsController}
        getThreadLinkUrl={editorRoute.getCommentThreadLinkUrl}
        open={isCollaborationPanelOpen}
        onClose={() => setOpenSidePanel(null)}
        suggestionsController={suggestionsController}
      />
      <TiptapVersionsPanel
        isLoading={isTiptapEditorLoading || isAwaitingNewRevisionHead}
        open={isVersionsPanelOpen}
        onClose={() => setOpenSidePanel(null)}
      >
        {editorVersions.versionCards}
      </TiptapVersionsPanel>
    </PanelGroup>
  );
};
